import { combineReducers } from 'redux'
import { unionBy, omit } from 'lodash'

import {
  FETCH_MAP_LIST,
  FETCH_LAYER_OPTIONS,
  SET_CURRENT_MAP,
  ADD_SIDEBAR_ITEM,
  REMOVE_SIDEBAR_ITEM,
  UPDATE_SIDEBAR_COLUMNS,
  RESET_MAP,
  REMOVE_SIDEBAR_ITEM_BY_LAYER_ID,
  REMOVE_REPORT_POI_BY_POI_ID,
  NEW_CENTER,
  SET_BOUNDS,
  UPDATE_TABLE_HEIGHT,
  TOGGLE_STREETS,
  FETCH_PUBLIC_POI_OPTIONS,
  FETCH_CUSTOM_POI_OPTIONS,
  ADD_DRAWN_FEATURE,
  UPDATE_DRAWN_FEATURE,
  DELETE_DRAWN_FEATURE,
  SET_POP_DOWN,
  ATTEMPT_GEO_QUERY,
  ADD_GEO_QUERY_RESULTS,
  DELETE_GEO_QUERY_RESULTS,
  ENABLE_POPUP,
  DISABLE_POPUP,
  UPDATE_POPUP,
  SET_UNSAVED_CHANGES,
  CHANGE_INTERACTION_LAYER,
  SET_FEATURE_STATE,
  CLEAR_SET_FEATURE_STATE,
  SET_DB_WIDGETS,
} from '../../constants/types'
import { LAYER_TYPE_DEMOGRAPHIC } from '../../constants/layer-types'
import layers from './layers/layer-reducers'


const mapList = (state = [], { type, payload }) => {
  switch (type) {
  case FETCH_MAP_LIST:
    return payload
  default:
    return state
  }
}

const layerOptions = (state = [], { type, payload }) => {
  switch (type) {
  case FETCH_LAYER_OPTIONS:
    return payload
  case RESET_MAP:
    return []
  default:
    return state
  }
}

const publicPoiLists = (state = [], { type, payload }) => {
  switch (type) {
  case FETCH_PUBLIC_POI_OPTIONS:
    return payload
  case RESET_MAP:
    return []
  default:
    return state
  }
}

const customPoiLists = (state = [], { type, payload }) => {
  switch (type) {
  case FETCH_CUSTOM_POI_OPTIONS:
    return payload
  case RESET_MAP:
    return []
  default:
    return state
  }
}

const currentMap = (state = {}, { type, payload }) => {
  switch (type) {
  case SET_CURRENT_MAP:
    return payload
  case RESET_MAP:
    return {}
  default:
    return state
  }
}

const sideBar = (state = [], { type, payload }) => {
  switch (type) {
  case ADD_SIDEBAR_ITEM:
    return unionBy([payload], state, 'id')
  case RESET_MAP:
    return []
  case REMOVE_SIDEBAR_ITEM:
    return state.filter((click) => click.id !== payload)
  case REMOVE_SIDEBAR_ITEM_BY_LAYER_ID:
    return state.filter(({ layerID }) => layerID !== payload)
  case REMOVE_REPORT_POI_BY_POI_ID:
    return state.filter(({ type, id }) => type !== 'poi' || id !== payload)
  default:
    return state
  }
}

const sideBarColumns = (state = 1, { type, payload }) => {
  switch (type) {
  case UPDATE_SIDEBAR_COLUMNS:
    return payload
  default:
    return state
  }
}

const mapCenterInitState = { lat: null, lng: null, count: 0 }
const mapCenter = (state = mapCenterInitState, { type, payload }) => {
  switch (type) {
  case NEW_CENTER:
    return {
      ...payload,
      count: state.count + 1,
    }
  case RESET_MAP:
    return mapCenterInitState
  default:
    return state
  }
}

const mapBounds = (state = {}, { type, payload }) => {
  switch (type) {
  case SET_BOUNDS:
    return payload
  default:
    return state
  }
}

const showStreets = (state = false, { type }) => {
  switch (type) {
  case TOGGLE_STREETS:
    return !state
  default:
    return state
  }
}

const mapPopDown = (state = { visible: false }, { type, payload }) => {
  switch (type) {
  case SET_POP_DOWN:
    return {
      visible: payload.visible || false,
      header: payload.header || '',
      description: payload.description || '',
    }
  default:
    return state
  }
}

const tableHeight = (state = 0, { type, payload }) => {
  switch (type) {
  case UPDATE_TABLE_HEIGHT:
    return payload
  case RESET_MAP:
    return 0
  default:
    return state
  }
}

const drawnFeatures = (state = {}, { type, payload }) => {
  switch (type) {
  case ADD_DRAWN_FEATURE:
    return {
      ...state,
      [payload.properties.id]: payload,
    }
  case UPDATE_DRAWN_FEATURE:
    return {
      ...state,
      [payload.id]: {
        ...state[payload.id],
        [payload.key]: payload.update,
      },
    }
  case DELETE_DRAWN_FEATURE:
    return omit(state, payload)
  case RESET_MAP:
    return {}
  default:
    return state
  }
}

const geoQueryResults = (state = {}, { type, payload }) => {
  switch (type) {
  case ADD_GEO_QUERY_RESULTS:
    return {
      ...state,
      [payload.id]: { ...payload.data },
    }
  case DELETE_GEO_QUERY_RESULTS:
    return omit(state, payload.id)
  default:
    return state
  }
}

const geoQueryAttempts = (state = {}, { type, payload }) => {
  switch (type) {
  case ATTEMPT_GEO_QUERY: {
    let value = state[payload.id]
    if (value !== undefined) {
      value += 1
    } else {
      value = 1
    }
    return {
      ...state,
      [payload.id]: value,
    }
  }
  default:
    return state
  }
}

/*
  Popup content format:
  [{
    type: 'SimplePieChart',
    ...props
  }]
*/
const popup = (state = { enabled: false, content: [], coordinates: [] }, { type, payload }) => {
  switch (type) {
  case ENABLE_POPUP:
    return {
      ...state,
      enabled: true,
    }
  case DISABLE_POPUP:
    return {
      ...state,
      enabled: false,
    }
  case UPDATE_POPUP: {
    const filteredContent = [...state.content].filter((c) => c.layer_id !== payload.layer_id)
    const newContent = payload.content ? payload.content.map((c) => ({ ...c, layer_id: payload.layer_id })) : []
    return {
      ...state,
      content: [...filteredContent, ...newContent],
      coordinates: payload.coordinates || state.coordinates,
    }
  }
  default:
    return state
  }
}

const unsavedChanges = (state = {}, { type, payload }) => {
  switch (type) {
  case SET_UNSAVED_CHANGES:
    if (payload.layerType === LAYER_TYPE_DEMOGRAPHIC) {
      return {
        ...state,
        [payload.layerType]: {
          ...state[payload.layerType],
          [payload.layerID]: payload.value,
        },
      }
    }
    return {
      ...state,
      [payload.layerType]: payload.value,
    }
  default:
    return state
  }
}

const interactionLayer = (state = 'none', { type, payload }) => {
  switch (type) {
  case CHANGE_INTERACTION_LAYER:
    return payload
  default:
    return state
  }
}

const pendingSetFeatureState = (state = false, { type, payload }) => {
  switch (type) {
  // required to trigger changes to map features outside of the scope of mapbox-map.js
  // e.g. hovering a sidebar item, dismissing
  // on map hover and onClick can be done by the map directly
  // will run map.setFeatureState()
  case SET_FEATURE_STATE:
    return { ...payload }
  case CLEAR_SET_FEATURE_STATE:
    return false
  default:
    return state
  }
}

const dbWidgets = (state = [], { type, payload }) => {
  switch (type) {
  case SET_DB_WIDGETS:
    return payload
  default:
    return state
  }
}

export default combineReducers({
  mapList,
  layerOptions,
  publicPoiLists,
  customPoiLists,
  layers,
  currentMap,
  sideBar,
  sideBarColumns,
  mapCenter,
  mapBounds,
  tableHeight,
  showStreets,
  drawnFeatures,
  mapPopDown,
  geoQueryResults,
  geoQueryAttempts,
  popup,
  unsavedChanges,
  interactionLayer,
  pendingSetFeatureState,
  dbWidgets,
})
